<template>
  <!-- Root wrapper; `fieldClass` toggles modifier classes for readonly/password/border/gray/search/etc. states -->
  <div :class="fieldClass" class="new-base-field">
    <!-- Field label, only rendered when a `label` prop is supplied -->
    <label v-if="label" class="new-base-field__label">
      {{ label }}
      <!-- Required-field asterisk, shown only when `isRequired` is true -->
      <span v-if="isRequired" class="new-base-field_required">*</span>
    </label>
    <div class="new-base-field__control">
      <!-- <div v-if="isSearch" class="mr-2 flex justify-center items-center search-icon-container">
        <SearchIcon />
      </div> -->
      <!--
        Main input. Its displayed value depends on mode:
        - datepicker mode: shows the formatted `calendarValue` (or blank) instead of the raw `value` prop
        - normal mode: shows `value` directly
        Readonly in datepicker mode (typing is disallowed; the calendar picker is used instead).
        @click opens the calendar (datepicker mode only); @input forwards typed text via newHandleInput;
        @paste is intercepted by newHandlePaste to block non-numeric paste when isNumericOnly is set.
      -->
      <input :value="isDatepicker ? (calendarValue ? new Date(calendarValue).toLocaleDateString('en-US') : '') : value"
        class="new-base-field__input" :autocomplete="isDatepicker ? 'off' : 'on'" :placeholder="placeholder"
        :readonly="isReadonly || isDatepicker" :disabled="disabled" :name="name" :type="type" :maxlength="maxLength"
        :inputmode="isNumericOnly ? 'numeric' : null" @click="newToggleCalendar" @input="newHandleInput"
        @paste="newHandlePaste" />

      <!-- Search icon, shown only when this field is rendered as a search input (`isSearch`) -->
      <div v-if="isSearch" class="flex justify-center items-center search-icon-container pr-2">
        <SearchIcon />
      </div>
      <!--
        PrimeVue Calendar popup, rendered only in datepicker mode while `showCalendar` is true.
        v-model binds to `calendarValue`; date-select fires newHandleDateSelect to commit the pick
        and close the popup; v-click-outside closes it (newCloseCalendar) when clicking elsewhere.
      -->
      <Calendar v-if="isDatepicker && showCalendar" v-model="calendarValue" @input="newHandleTetstDateSelect" inline
        :maxDate="maxDate" :minDate="minDate" appendTo="body" dateFormat="dd/mm/yy" @date-select="newHandleDateSelect"
        :monthNavigator="true" v-click-outside="newCloseCalendar" />
      <div class="new-base-field__slot">
        <button type="button">
          <!-- Show/hide password toggle icon, shown only for password fields (`isPassword`) -->
          <span v-if="isPassword" class="ui-kit__icon" :class="passwordFieldIcon" @click="newShowPassword"></span>
          <!-- Clear ("x") icon for search fields with a non-empty value (hidden in org-picker mode) -->
          <span v-if="isSearch && value && !isOrg" @click="$emit('update:value', '')"
            class="ui-kit__icon text-foregroundPrimary-new isSearchIcon">
            &times;
          </span>
          <!-- Clear ("x") icon for any field marked `isClearable` with a non-empty value (hidden in org-picker mode) -->
          <span v-if="isClearable && value && !isOrg" @click="$emit('update:value', '')"
            class="ui-kit__icon text-foregroundPrimary-new">
            &times;
          </span>
          <!-- Org-picker mode: "x" clears selected members via the `clearMembers` emit instead of `update:value` -->
          <span v-if="isOrg" @click="$emit('clearMembers')" class="ui-kit__icon text-foregroundPrimary-new"> &times; </span>
          <!-- Static checkmark icon shown when `isCheckIcon` is set (e.g. to indicate a validated field) -->
          <span v-if="isCheckIcon" class="ui-kit__icon text-foregroundPrimary-new cursor-default">
            <CircleCheckIcon />
          </span>
        </button>
        <!-- Named slot letting a parent inject extra controls (e.g. custom buttons) next to the icons -->
        <slot name="button"></slot>
      </div>
    </div>
    <!-- Animates error messages in/out as `errorsList` changes; loops over one or more validation errors -->
    <TransitionGroup>
      <div class="new-base-field__error" v-for="(item, idx) of errorsList" :key="idx">
        <span class="new-base-field__error-message">{{ formatError(item) }}</span>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';

import { directive as vClickOutside } from 'click-outside-vue3';
import Calendar from 'primevue/calendar';
import { icons } from '@tiptap/pm/menu';
import SearchIcon from '@/components/Icons/SearchIcon.vue';
import CircleCheckIcon from '@/components/Icons/CircleCheckIcon.vue';

/**
 * @emits update:value - Fired with the new string value whenever the input changes (typing or date selection), enabling `v-model:value`.
 * @emits showPassword - Fired with the input's current DOM value when the password show/hide icon is clicked.
 * @emits clearMembers - Fired when the clear ("x") icon is clicked in org-picker mode (`isOrg`), telling the parent to clear selected members.
 */
const emit = defineEmits(['update:value', 'showPassword', 'clearMembers']);
/**
 * @prop {String} value - The field's current value (controlled from the parent).
 * @prop {String} text - Unused display text prop reserved for callers.
 * @prop {String} label - Optional label text shown above the input.
 * @prop {String} placeholder - Placeholder text for the input.
 * @prop {String} type - HTML input type (e.g. 'text', 'password').
 * @prop {String} name - HTML `name` attribute for the input.
 * @prop {Number} maxLength - Optional max character length for the input.
 * @prop {Array} error - List of validation errors (strings or `{ $message }` objects) to render below the field.
 * @prop {Boolean} isPassword - Shows the password show/hide toggle icon.
 * @prop {Boolean} isReadonly - Makes the input readonly and applies the readonly styling.
 * @prop {Boolean} isTextCenter - Centers the input text.
 * @prop {Boolean} isGray - Applies the gray background variant.
 * @prop {Boolean} isRequired - Shows a required-field asterisk next to the label.
 * @prop {Boolean} isBorder - Applies the bordered variant styling.
 * @prop {Boolean} isBottomBorderOnFocus - Applies the underline-on-focus variant styling.
 * @prop {Boolean} isSearch - Renders as a search field (search icon + search styling).
 * @prop {Boolean} isOrg - Org-picker mode; changes the clear icon's behavior/emit.
 * @prop {Boolean} isClearable - Shows a generic clear ("x") icon when there's a value.
 * @prop {Boolean} isDatepicker - Switches the field into date-picker mode (readonly input + Calendar popup).
 * @prop {Boolean} isCheckIcon - Shows a static checkmark icon (e.g. to indicate validation success).
 * @prop {Boolean} isNumericOnly - Restricts typed/pasted input to digits only.
 * @prop {Boolean} disabled - Disables the input.
 */
const props = defineProps({
  value: {
    type: String,
    default: '',
  },
  text: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: 'text',
  },
  name: {
    type: String,
    default: '',
  },
  maxLength: {
    type: Number,
    default: null,
  },
  error: {
    type: Array,
    required: false,
  },
  isPassword: {
    type: Boolean,
    default: false,
  },
  isReadonly: {
    type: Boolean,
    default: false,
  },
  isTextCenter: {
    type: Boolean,
    default: false,
  },
  isGray: {
    type: Boolean,
    default: false,
  },
  isRequired: {
    type: Boolean,
    default: false,
  },
  isBorder: {
    type: Boolean,
    default: false,
  },
  isBottomBorderOnFocus: {
    type: Boolean,
    default: false,
  },
  isSearch: {
    type: Boolean,
    default: false,
  },
  isOrg: {
    type: Boolean,
    default: false,
  },
  isClearable: {
    type: Boolean,
    default: false,
  },
  isDatepicker: {
    type: Boolean,
    default: false,
  },
  isCheckIcon: {
    type: Boolean,
    default: false,
  },
  isNumericOnly: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});
// PrimeVue Calendar expects Date or Date[]; keep internal as Date | null
/** Internal Date representation of the field's value, used only in datepicker mode. */
const calendarValue = ref(props.value ? new Date(props.value) : null);
/** Whether the Calendar popup is currently visible (datepicker mode). */
const showCalendar = ref(false);
/** Latest selectable date for the Calendar popup (today). */
const maxDate = ref(new Date());
/** Earliest selectable date for the Calendar popup (Jan 1, 1900). */
const minDate = ref(new Date(1900, 0, 1));

/**
 * Keeps `calendarValue` in sync whenever the parent updates the `value` prop
 * externally, but only while in datepicker mode.
 */
watch(
  () => props.value,
  newValue => {
    if (props.isDatepicker) {
      calendarValue.value = newValue ? new Date(newValue) : null;
    }
  },
);

/** Computes the modifier classes applied to the root element based on the boolean style/state props. */
const fieldClass = computed(() => {
  return [
    {
      'new-base-field_is-readonly': props.isReadonly,
      'new-base-field_is-password': props.type === 'password',
      'new-base-field_is-center': props.isTextCenter,
      'new-base-field_is-border': props.isBorder,
      'new-base-field_is-gray': props.isGray,
      'new-base-field_is-search': props.isSearch,
      'new-base-field_is-bottom-border-focus': props.isBottomBorderOnFocus,
      'new-base-field_is-disabled': props.disabled,
    },
  ];
});

/** Chooses the eye/eye-off icon class based on the current `type` prop (password vs. revealed text). */
const passwordFieldIcon = computed(() => {
  return props.type === 'password' ? 'icon-eye-off' : 'icon-eye';
});

// Normalize errors to strings for rendering and to satisfy TS template inference
/** Normalizes `props.error` to an array, guarding against non-array or missing values, for safe v-for rendering. */
const errorsList = computed(() => Array.isArray(props.error) ? props.error : []);
/**
 * Formats a single error entry for display: extracts `$message` from validator-style
 * objects, otherwise coerces the value to a string.
 * @param {*} item - Raw error entry (string or `{ $message }` object).
 * @returns {string} Human-readable error text, or '' if it can't be formatted.
 */
const formatError = (item) => {
  try {
    if (item && typeof item === 'object' && '$message' in item) {
      return item.$message;
    }
    return String(item ?? '');
  } catch {
    return '';
  }
};

/**
 * Blocks pasting non-numeric text into the field when `isNumericOnly` is set.
 * @param {*} e - Native paste (clipboard) event.
 */
const newHandlePaste = e => {
  if (!props.isNumericOnly) return;

  const cd = e.clipboardData;
  const pasted = cd ? cd.getData('text') : '';
  if (!/^\d+$/.test(pasted)) {
    e.preventDefault();
  }
};

/**
 * Handles native `input` events on the text field: strips non-digit characters
 * when `isNumericOnly` is set, then emits the resulting value via `update:value`.
 * @param {*} e - Native input event.
 */
const newHandleInput = e => {
  let val = e.target.value;
  if (props.isNumericOnly) {
    val = val.replace(/\D/g, '');
    e.target.value = val;
  }
  emit('update:value', val);
};

/**
 * Emits `showPassword` with the input's current value when the eye icon is clicked
 * (the parent is expected to use this to toggle the `type` prop between text/password).
 * @param {*} e - Native click event.
 */
const newShowPassword = e => {
  emit('showPassword', e.target.value);
};
/** Opens/closes the Calendar popup when the input is clicked, only in datepicker mode. */
const newToggleCalendar = () => {
  if (props.isDatepicker) {
    showCalendar.value = !showCalendar.value;
  }
};
/** Closes the Calendar popup (used by the click-outside directive and after a date is picked). */
const newCloseCalendar = () => {
  showCalendar.value = false;
};

/** No-op handler bound to the Calendar's `@input`; date commits are handled by newHandleDateSelect instead. */
const newHandleTetstDateSelect = () => { };
/**
 * Fires when the user picks a date in the Calendar popup: formats `calendarValue`
 * as a US-locale date string, emits it via `update:value`, and closes the popup.
 */
const newHandleDateSelect = () => {
  const d = calendarValue.value instanceof Date ? calendarValue.value : null;
  const formattedDate = d ? d.toLocaleDateString('en-US') : '';
  emit('update:value', formattedDate);
  newCloseCalendar();
};
</script>

<style lang="scss" scoped>
.isSearchIcon {
  font-size: 26px;
  opacity: 0.6;
  transition: 0.3s;

  &:hover {
    opacity: 1;
  }
}

.new-base-field {
  position: relative;
  font-size: 20px;
  // margin-bottom: 10px;

  &__label {
    // color: var(--color-text-02);
    color: var(--color-accent-primary);
    min-height: 30px;
    margin-bottom: 0;
    font-size: 14px !important;
    font-weight: 700;
  }

  &_required {
    color: var(--color-brand-secondary);
  }

  &__control {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    // height: 48px;
    height: 36px;
    padding: 0 15px;
    line-height: 145%;
    // border: 1px solid var(--color-border-subtle);
    border: 1px solid var(--color-new-input-field-border) !important;
    background: var(--color-input-field-bg) !important;
    // border-radius: 12px;
    border-radius: 5.28px;
    transition: 0.2s ease-in-out;

    &:focus-within {
      border-color: var(--color-input-field-border);
    }
  }

  &__input {
    position: relative;
    width: 100%;
    border: none;
    background: transparent;
    color: var(--color-brand-secondary);
    font-size: 16px;

    &::placeholder {
      color: var(--color-brand-secondary);
      font-size: 16px;
    }
  }

  &__slot {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 4px 0;
    height: 100%;

    span {
      width: 25px;

      &:before {
        font-size: 26px;
      }
    }
  }

  &__error {
    margin-top: 2px;
    border-radius: 10px;
    font-size: 12px;
    color: var(--color-brand-secondary);
    padding: 2px 10px;
  }

  .ui-kit {
    &__icon {
      &::before {
        font-size: 20px;
      }
    }
  }

  &_is-password {
    &:focus-within {
      .ui-kit {
        &__icon {
          &::before {
            color: var(--color-brand-secondary);
          }
        }
      }
    }
  }

  &_is-disabled {
    .new-base-field__control {
      opacity: 0.5;
      cursor: no-drop;
    }

    .new-base-field__input {
      cursor: no-drop;
    }
  }

  &_is-readonly {
    .new-base-field__control {
      background: rgba(255, 255, 255, 0.05);
      cursor: no-drop;
    }

    .new-base-field__input {
      &:read-only {
        color: var(--color-text-02);
        cursor: no-drop;
      }
    }
  }

  &_is-border {
    .new-base-field__control {
      border: 1px solid var(--color-border-subtle);

      &:focus-within {
        border-color: var(--color-brand-secondary);
      }
    }
  }

  &_is-bottom-border-focus {
    gap: 8px;
    display: flex;
    flex-direction: column;

    .new-base-field__label {
      font-size: 14px;
      line-height: 16px;
      min-height: auto;
    }

    .new-base-field__control {
      // background: var(--color-background-secondary);
      border: none;
      border-bottom: 1px solid transparent;
      transition: border-bottom 0.3s ease;
    }

    .new-base-field__control:focus-within {
      border: none;
      border-bottom: 1px solid var(--color-foreground-primary);
    }

    .new-base-field__input {
      font-size: 16px;
      line-height: 20px;
      color: var(--color-foreground-primary);
    }

    .new-base-field__input::placeholder {
      font-size: 16px;
      line-height: 20px;
      /* OLD: color: #9696a2; */
      color: var(--color-grey-quinary);
    }

    .new-base-field__error {
      /* OLD: color: #aa2c4e; */
      color: var(--color-brand-secondary);
      padding: 0;
    }

    .new-base-field__control:has(+ .new-base-field__error) {
      border: none;
      /* OLD: border-bottom: 1px solid #aa2c4e; */
      border-bottom: 1px solid var(--color-brand-secondary);
    }
  }

  &_is-search {
    .new-base-field__control {
      background: var(--color-search-bg) !important;
      // background: #3E4049;
      // border-color: var(--color-surface-border, var(--color-input-field-border));
      border-color: var(--color-button-border) !important;
      box-shadow: inset 0 0 0 1px var(--color-surface-border, var(--color-search-bg));
      transition: background .15s ease, box-shadow .15s ease, border-color .15s ease;
      border-radius: 10px;
    }

    .new-base-field__control:focus-within {
      border-color: var(--color-input-field-border-v2);
    }

    .new-base-field__control:hover:not(:focus-within) {
      box-shadow: inset 0 0 0 1px var(--border-primary, #cbd5e1);
      // background: var(--color-input-field-border);
    }

    .new-base-field__input {
      color: var(--color-search-text);
    }

    .new-base-field__input::placeholder {
      // color: var(--color-brand-secondary);
      color: var(--color-text-white-black);
      font-size: 12px;
    }

    .new-base-field__control:focus-within {
      background: var(--color-background-primary);
    }

    /* Dark mode refinement */
    [scheme='dark'] & .new-base-field__control {
      // background: rgba(255, 255, 255, 0.05);
      /* OLD: background: #3E4049; */
      background: var(--color-theme-wise-background);
      box-shadow: inset 0 0 0 1px var(--color-surface-border, #334155);
      /* OLD: border: 1px solid #71A8AC; */
      border: 1px solid var(--color-box-border);
    }

    [scheme='dark'] & .new-base-field__control:hover:not(:focus-within) {
      background: rgba(255, 255, 255, 0.08);
    }

    [scheme='dark'] & .new-base-field__control:focus-within {
      background: var(--color-background-secondary);
      // box-shadow: 0 0 0 2px rgba(132, 204, 22, 0.35), inset 0 0 0 1px var(--color-brand-secondary);
    }
  }

  &_is-gray {
    .new-base-field__control {
      background: var(--color-background-secondary);
    }
  }

  &_is-center &__input {
    text-align: center;
  }

  &_is-center {
    min-width: 100px;
  }

  @include _380 {
    font-size: 14px;

    &__control {
      font-size: 14px;
      height: 44px;
    }
  }
}



.p-calendar {
  position: absolute;
  z-index: 1000;
  bottom: 50px;
  left: 50%;
  transform: translateX(-50%);

  &>* {
    color: var(--color-foreground-primary);
  }
}

.p-calendar>.p-datepicker-calendar tbody>tr>td>span {
  background-color: var(--color-background-secondary) !important;
}

.icon-eye {
  background: url('/icon/eye.svg') no-repeat center;
  width: 16px;
  height: 16px;
  display: block;
}

.icon-eye-off {
  background: url('/icon/eye-off.svg') no-repeat center;
  width: 16px;
  height: 16px;
  display: block;
}

.search-icon-container {
  color: var(--color-text-01);
  opacity: 0.8;

  /* Ensure better visibility in light mode */
  [scheme='light'] & {
    opacity: 0.9;
    color: var(--color-foreground-primary);
  }

  /* Maintain good visibility in dark mode */
  [scheme='dark'] & {
    opacity: 0.7;
    color: var(--color-text-01);
  }
}
.new-base-field_required {
  color: var(--color-form-input-border-error);
}
</style>
